import { ClickAwayListener, List, ListProps, Paper, Popper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useMemo, useRef, useState } from 'react';
import { TextInput, TextInputExtendedProps } from '../TextInput';
import { SearchAndSelectOption } from './SearchAndSelectOption.component';

export type SearchAndSelectType<T> = {
  name: string;
  label: string;
  options?: T[];
  children?: React.ReactNode;
  getOptionLabel?: (option: T) => string;
  listProps?: ListProps;
  renderListItem?: (option: T) => React.ReactNode;
  onOptionSelect?: (option: T) => void;
  popperConstraint?: boolean;
  width?: string;
  maxWidth?: string;
} & TextInputExtendedProps;

export const SearchAndSelect = <T extends { id: string }>(
  {
    name,
    label,
    options,
    children,
    getOptionLabel,
    listProps = {},
    renderListItem,
    onOptionSelect,
    popperConstraint = true,
    ...props
  }: SearchAndSelectType<T>) => {
  const ref = useRef<HTMLDivElement>(null);
  const [ anchorEl, setAnchorEl ] = useState<HTMLDivElement | null>(null);
  const { classes, cx } = useStyles({ offsetWidth: anchorEl?.offsetWidth });

  const handleFocus = () => {
    if (ref.current) {
      setAnchorEl(ref.current);
    }
  };

  const handleBlur = () => {
    setAnchorEl(null);
  };

  const customContent = children !== undefined;

  const width = useMemo(() => {
    if (props.fullWidth) {
      return '100%';
    }

    if (props.width) {
      return props.width;
    }

    return;
  }, [ props.fullWidth, props.width ]);

  if (customContent && options !== undefined) {
    throw new Error('Pass in either options or children; not both');
  } else if (!customContent && options === undefined) {
    throw new Error('Pass in either options or children');
  }

  const { className: listClassName, ...restListProps } = listProps;

  return (
    (
      <ClickAwayListener
        onClickAway={handleBlur}
        // FIX for Clickawaylistener doesn't fire on buttons and links
        // https://github.com/mui/material-ui/issues/17402#issuecomment-1013342932
        mouseEvent="onMouseDown"
        touchEvent="onTouchStart"
      >
        <div
          ref={ref}
          style={{
            display: 'inline-block',
            maxWidth: props.maxWidth ? props.maxWidth : undefined,
            width: width
          }}
        >
          <TextInput
            id={name}
            name={name}
            label={label}
            onFocus={handleFocus}
            autoComplete="off"
            fullWidth
            {...props}
          />
          {anchorEl && popperConstraint && (
            <Popper
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              className={classes.popper}
            >
              <Paper onClick={handleBlur} className={classes.paper}>
                {!customContent && options && (
                  <List className={cx([ classes.list, listClassName ])} {...restListProps}>
                    {options.map((option, i) => {
                      return (
                        <SearchAndSelectOption
                          key={i}
                          option={option}
                          getOptionLabel={getOptionLabel}
                          renderListItem={renderListItem}
                          onOptionSelect={onOptionSelect}
                        />
                      );
                    })}
                  </List>
                )}
                {customContent && children}
              </Paper>
            </Popper>
          )}
        </div>
      </ClickAwayListener>
    )
  );
};

type StyleProps = {
  offsetWidth?: number;
};

const useStyles = makeStyles<StyleProps>()((theme, { offsetWidth }) => ({
  popper: {
    width: offsetWidth ? `${offsetWidth}px` : 0,
    zIndex: theme.zIndex.modal + 1,
  },
  paper: {
    overflow: 'hidden',
  },
  list: {
    overflow: 'auto',
    maxHeight: '40vh',
  },
}));
